// crypto.ts

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

// const key = crypto.getRandomValues(new Uint8Array(32));
// console.log(btoa(String.fromCharCode(...key)));


// ⚠️ Must be defined in your .env
const BASE64_KEY = process.env.REACT_APP_ENCRYPT_KEY as string;
// If using CRA instead: process.env.REACT_APP_SHARED_KEY

if (!BASE64_KEY) {
    throw new Error("Missing VITE_SHARED_KEY in environment variables");
}

// ---------- Helpers ----------

function base64ToUint8(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
}

function uint8ToBase64(bytes: Uint8Array): string {
    let binary = "";
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
        const sub = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(sub));
    }

    return btoa(binary);
}

// ---------- Key (cached) ----------

let cachedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey> {
    if (cachedKey) return cachedKey;

    const keyBytes = base64ToUint8(BASE64_KEY);

    cachedKey = await crypto.subtle.importKey(
        "raw",
        keyBytes,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
    );

    return cachedKey;
}

// ---------- Encrypt ----------

export async function encryptPayload(data: unknown): Promise<string> {
    const key = await getKey();

    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encoded = textEncoder.encode(JSON.stringify(data));

    const cipherBuffer = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        encoded
    );

    const combined = new Uint8Array(iv.length + cipherBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipherBuffer), iv.length);

    return uint8ToBase64(combined);
}

// ---------- Decrypt ----------

export async function decryptPayload<T = any>(
    encrypted: string
): Promise<T> {
    const key = await getKey();

    const combined = base64ToUint8(encrypted);

    const iv = combined.slice(0, 12);
    const cipherText = combined.slice(12);

    const decryptedBuffer = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        key,
        cipherText
    );

    const text = new TextDecoder().decode(decryptedBuffer);

    // ✅ Try parsing JSON safely
    try {
        return JSON.parse(text);
    } catch {
        return text as T; // fallback for plain strings
    }
}